import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, RefreshCw,
  AlertCircle, CheckCircle,
  Calendar, IndianRupee, TrendingUp,
  Target, PieChart, BarChart3, CreditCard,
  Settings, ClipboardList, BarChart2,
  Wallet, Smartphone, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';
import { 
  getAllBillingInvoices, 
  getBillingServices, 
  createBillingInvoice,
  updateBillingInvoice,
  recordInvoicePayment,
  getBillingAnalytics,
  getDoctors
} from '@/lib/api';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import InvoiceEditModal from '@/components/Patient/InvoiceEditModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { InvoiceCard } from '@/components/Billing/InvoiceCard';
import { BillingListContainer } from '@/components/Billing/BillingUI';
import BillingServicesTab from '@/components/Billing/BillingServicesTab';
import { InvoiceViewModal, PaymentRecordModal } from '@/components/Billing/BillingModals';
import type { Invoice, BillingService, BillingAnalytics, Patient } from '@/types';

export default function BillingPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { document.title = "Billing | OneCheckUp"; }, []);

  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [services, setServices] = useState<BillingService[]>([]);
  const [analytics, setAnalytics] = useState<BillingAnalytics | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const [amountRange, setAmountRange] = useState({ min: '', max: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedService, setSelectedService] = useState<BillingService | null>(null);
  const [billingDoctors, setBillingDoctors] = useState<{id: string; name: string}[]>([]);
  
  // Payment modal states
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  
  // Use shared billing utilities
  const { 
    formatCurrency, 
    getStatusConfig
  } = useBillingUtils();

  // Simple notification helper using existing toast
  const showNotification = (type: 'success' | 'error', message: string) => {
    toast({
      title: type === 'success' ? 'Success' : 'Error',
      description: message,
      variant: type === 'success' ? 'default' : 'destructive',
    });
  };

  // Computed values using useMemo
  const filteredInvoices = React.useMemo(() => {
    return invoices.filter(invoice => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        invoice.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (invoice.patient_phone && invoice.patient_phone.includes(searchTerm)) ||
        (invoice.doctor_name && invoice.doctor_name.toLowerCase().includes(searchTerm.toLowerCase()));

      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        invoice.payment_status.toLowerCase() === statusFilter.toLowerCase();
      
      // Date filter
      const invoiceDate = new Date(invoice.date);
      const now = new Date();
      const invoiceDateOnly = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate());
      const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const weekStartOnly = new Date(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth(), sevenDaysAgo.getDate());
      const monthStartOnly = new Date(now.getFullYear(), now.getMonth(), 1);
      
      let matchesDate = dateFilter === 'all' ||
        (dateFilter === 'today' && invoiceDateOnly.getTime() === todayOnly.getTime()) ||
        (dateFilter === 'week' && invoiceDateOnly >= weekStartOnly) ||
        (dateFilter === 'month' && invoiceDateOnly >= monthStartOnly);

      // Date range filter (overrides quick date filter)
      if (dateRange.from || dateRange.to) {
        const invoiceDateOnly = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate());
        
        if (dateRange.from && dateRange.to) {
          const fromDate = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
          const toDate = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
          matchesDate = invoiceDateOnly >= fromDate && invoiceDateOnly <= toDate;
        } else if (dateRange.from) {
          const fromDate = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
          matchesDate = invoiceDateOnly >= fromDate;
        } else if (dateRange.to) {
          const toDate = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
          matchesDate = invoiceDateOnly <= toDate;
        }
      }

      // Amount range filter
      const matchesAmountRange = (!amountRange.min || invoice.total_amount >= parseFloat(amountRange.min)) &&
        (!amountRange.max || invoice.total_amount <= parseFloat(amountRange.max));

      return matchesSearch && matchesStatus && matchesDate && matchesAmountRange;
    });
  }, [invoices, searchTerm, statusFilter, dateFilter, dateRange, amountRange]);

  // Load initial data - clinic_id is required, doctor_id is optional (for receptionists)
  useEffect(() => {
    if (currentUser?.clinic_id) {
      loadAllData();
    }
  }, [currentUser?.clinic_id, currentUser?.doctor_id]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, dateRange, amountRange]);

  // Validate required user data - only clinic_id is required (receptionists don't have doctor_id)
  if (!currentUser?.clinic_id) {
    return (
      <div className="p-6 bg-background min-h-screen">
        <Alert className="status-critical">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            User must have clinic_id to access billing.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        loadInvoices(),
        loadServices(),
        loadAnalytics()
      ]);
    } catch (error) {
      console.error('Error loading billing data:', error);
      showNotification('error', 'Failed to load billing data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await getAllBillingInvoices(currentUser.clinic_id);
      
      // Handle different response formats
      let invoicesArray = [];
      if (Array.isArray(data)) {
        invoicesArray = data;
      } else if (data && Array.isArray((data as any).invoices)) {
        invoicesArray = (data as any).invoices;
      } else if (data && (data as any).data && Array.isArray((data as any).data)) {
        invoicesArray = (data as any).data;
      } else {
        console.warn('Unexpected API response format:', data);
        invoicesArray = [];
      }
      
      setInvoices(invoicesArray);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      setInvoices([]); // Ensure invoices is always an array
      throw error;
    }
  };

  // Memoize loadServices to prevent infinite loops in child components
  const loadServices = useCallback(async () => {
    try {
      const data = await getBillingServices(currentUser?.clinic_id || '', currentUser?.doctor_id);
      
      // Handle different response formats
      let servicesArray = [];
      if (Array.isArray(data)) {
        servicesArray = data;
      } else if (data && Array.isArray((data as any).services)) {
        servicesArray = (data as any).services;
      } else if (data && (data as any).data && Array.isArray((data as any).data)) {
        servicesArray = (data as any).data;
      } else {
        console.warn('Unexpected services API response format:', data);
        servicesArray = [];
      }
      
      setServices(servicesArray);
    } catch (error) {
      console.error('Failed to load services:', error);
      setServices([]); // Ensure services is always an array
      // Don't throw - services are not critical for initial load
    }
  }, [currentUser?.clinic_id, currentUser?.doctor_id]);

  const loadAnalytics = async () => {
    try {
      const data = await getBillingAnalytics(currentUser.clinic_id);
      
      // Validate analytics data structure
      if (data && typeof data === 'object') {
        const analyticsData = data as any;
        setAnalytics({
          totalRevenue: Number(analyticsData.totalRevenue) || 0,
          monthlyRevenue: Number(analyticsData.monthlyRevenue) || 0,
          pendingAmount: Number(analyticsData.pendingAmount) || 0,
          totalInvoices: Number(analyticsData.totalInvoices) || 0,
          paidInvoices: Number(analyticsData.paidInvoices) || 0,
          pendingInvoices: Number(analyticsData.pendingInvoices) || 0,
          averageInvoiceValue: Number(analyticsData.averageInvoiceValue) || 0,
          topServices: Array.isArray(analyticsData.topServices) ? analyticsData.topServices : []
        });
      } else {
        console.warn('Unexpected analytics API response format:', data);
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setAnalytics(null);
      // Don't throw - analytics are not critical for initial load
    }
  };

  const refreshData = async () => {
    try {
      setIsRefreshing(true);
      await loadAllData();
      showNotification('success', 'Data refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh data:', error);
      showNotification('error', 'Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };



  const handleNewInvoice = async () => {
    const isStaff = currentUser?.role === 'staff';
    if (isStaff && billingDoctors.length === 0) {
      try {
        const docs = await getDoctors();
        setBillingDoctors(Array.isArray(docs) ? docs : []);
      } catch { setBillingDoctors([]); }
    }
    setShowCreateModal(true);
  };

  const handleOpenServiceModal = () => {
    setActiveTab('services');
    setShowServiceModal(true);
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      setIsLoading(true);

      const invoicePatient = invoiceData.patient;
      if (!invoicePatient?.id) {
        showNotification('error', 'Please select a patient first');
        return;
      }

      const apiInvoiceData = {
        patient_id: invoicePatient.id,
        patient_name: invoicePatient.name,
        patient_phone: invoicePatient.phone,
        patient_address: invoicePatient.address ?? "",
        doctor_id: invoiceData.doctorId || currentUser.doctor_id || '',
        clinic_id: currentUser.clinic_id,
        items: invoiceData.items,
        discount_amount: invoiceData.discount || 0,
        payment_method: invoiceData.paymentMethod,
        paid_amount: invoiceData.paidAmount ?? invoiceData.finalTotal,
        notes: invoiceData.discount > 0 ? `Discount applied: ₹${invoiceData.discount}` : undefined
      };

      await createBillingInvoice(apiInvoiceData);
      
      showNotification('success', 'Invoice created successfully');
      
      setShowCreateModal(false);
      await loadInvoices();
      await loadAnalytics();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create invoice';
      showNotification('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecordPayment = async (amount?: number, method?: string) => {
    try {
      const finalAmount = amount ?? paymentAmount;
      const finalMethod = method ?? paymentMethod;

      if (!selectedInvoice || finalAmount <= 0) {
        showNotification('error', 'Please enter a valid payment amount');
        return;
      }

      if (finalAmount > selectedInvoice.balance_amount) {
        showNotification('error', 'Payment amount cannot exceed balance due');
        return;
      }

      setIsLoading(true);
      await recordInvoicePayment(selectedInvoice.id, {
        amount: finalAmount,
        payment_method: finalMethod,
        payment_date: new Date().toISOString()
      });

      showNotification('success', 'Payment recorded successfully');
      setShowPaymentModal(false);
      setSelectedInvoice(null);
      setPaymentAmount(0);
      loadInvoices(); // Refresh data
      loadAnalytics(); // Update analytics
    } catch (error) {
      console.error('Failed to record payment:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to record payment';
      showNotification('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowEditModal(true);
  };

  const handleUpdateInvoice = async (invoiceData: any) => {
    try {
      if (!selectedInvoice) return;

      setIsLoading(true);
      await updateBillingInvoice(selectedInvoice.id, invoiceData);

      showNotification('success', 'Invoice updated successfully');
      setShowEditModal(false);
      setSelectedInvoice(null);
      loadInvoices(); // Refresh invoices list
      loadAnalytics(); // Update analytics
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      showNotification('error', error.message ?? 'Failed to update invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setPaymentAmount(invoice.balance_amount);
    setShowPaymentModal(true);
  };

  const handleViewPatient = (patientId: string) => {
    navigate(`/patients/${patientId}?tab=billing`);
  };

  const handlePrintInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowPrintModal(true);
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Loading state for initial data
  if (isLoading && invoices.length === 0) {
    return (
      <div className="prescription-form-container">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <Skeleton className="h-8 w-64 mb-2" />
              <Skeleton className="h-4 w-96" />
            </div>
            <div className="flex gap-3">
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
          
          <div className="space-y-4">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-96 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prescription-form-container">
      {/* Notifications handled by toast system */}

            {/* Header */}
      <div className="border-b border-border pb-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Billing Management</h1>
              <p className="text-sm text-muted-foreground">
                Manage invoices, payments & analytics • {invoices.length} total invoices
              </p>
            </div>
          </div>
          
          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={refreshData}
              disabled={isRefreshing}
              className="px-4"
              title="Refresh billing data"
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", isRefreshing && "animate-spin")} />
              Refresh
            </Button>
            
            <Button 
              onClick={handleOpenServiceModal}
              variant="outline"
              size="sm"
              className="px-4"
              title="Create new billing service"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
            
            <Button 
              onClick={handleNewInvoice} 
              size="sm" 
              className="px-4"
              title="Create new invoice"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              New Invoice
            </Button>
          </div>
        </div>
      </div>

            {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Revenue Card */}
        <Card className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 p-1.5 rounded bg-primary">
              <IndianRupee className="w-4 h-4 text-primary-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-foreground truncate">
                {analytics ? formatCurrency(analytics.totalRevenue) : formatCurrency(0)}
              </div>
              <div className="text-sm text-muted-foreground">Total Revenue</div>
            </div>
          </div>
        </Card>

        {/* Monthly Revenue Card */}
        <Card className="p-4 bg-gradient-to-br from-success/5 to-success/10 border-success/20">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 p-1.5 rounded bg-success">
              <TrendingUp className="w-4 h-4 text-background" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-foreground truncate">
                {analytics ? formatCurrency(analytics.monthlyRevenue) : formatCurrency(0)}
              </div>
              <div className="text-sm text-muted-foreground">This Month</div>
            </div>
          </div>
        </Card>

        {/* Outstanding Amount Card */}
        <Card className="p-4 bg-gradient-to-br from-warning/5 to-warning/10 border-warning/20">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 p-1.5 rounded bg-warning">
              <Target className="w-4 h-4 text-background" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-foreground truncate">
                {analytics ? formatCurrency(analytics.pendingAmount) : formatCurrency(0)}
              </div>
              <div className="text-sm text-muted-foreground">Outstanding</div>
            </div>
          </div>
        </Card>

        {/* Collection Rate Card */}
        <Card className="p-4 bg-gradient-to-br from-info/5 to-info/10 border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 p-1.5 rounded bg-primary">
              <PieChart className="w-4 h-4 text-primary-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-foreground truncate">
                {analytics ? `${(analytics.paidInvoices / Math.max(analytics.totalInvoices, 1) * 100).toFixed(1)}%` : 'N/A'}
              </div>
              <div className="text-sm text-muted-foreground">Collection Rate</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="p-1">
            <TabsTrigger value="invoices" className="px-4 py-2">
              <ClipboardList className="w-4 h-4 mr-2" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="analytics" className="px-4 py-2 hidden">
              <BarChart2 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="services" className="px-4 py-2">
              <Settings className="w-4 h-4 mr-2" />
              Services
            </TabsTrigger>
          </TabsList>
          
          <div className="text-sm text-muted-foreground">
            {filteredInvoices.length > itemsPerPage 
              ? `Page ${currentPage} of ${Math.ceil(filteredInvoices.length / itemsPerPage)} • ${filteredInvoices.length} total invoices`
              : `${filteredInvoices.length} invoices`
            }
          </div>
        </div>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          {/* Enhanced Search with Date Range */}
          <BillingListContainer
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            dateFilter={dateFilter}
            onDateChange={setDateFilter}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            amountRange={amountRange}
            onAmountRangeChange={setAmountRange}
            variant="clinic"
            invoices={invoices}
            filteredInvoices={filteredInvoices}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onCreateInvoice={handleNewInvoice}
          >
            {/* Invoices Grid */}
            <div className="space-y-3">
              {filteredInvoices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  variant="clinic"
                  onView={handleViewInvoice}
                  onEdit={handleEditInvoice}
                  onPrint={handlePrintInvoice}
                  onPayment={handlePayInvoice}
                  onViewPatient={handleViewPatient}
                  className="invoice-card"
                />
              ))}
            </div>
          </BillingListContainer>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4 hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Payment Method Distribution */}
            <Card className="medical-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-foreground text-sm">
                  <div className="w-5 h-5 bg-primary/10 rounded-md flex items-center justify-center">
                    <CreditCard className="w-3 h-3 text-primary" />
                  </div>
                  Payment Method Analytics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {['Cash', 'UPI', 'Card', 'Cheque', 'Insurance'].map((method) => {
                  const methodInvoices = invoices.filter(inv => inv.payment_method === method);
                  const methodRevenue = methodInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
                  const percentage = invoices.length > 0 ? (methodInvoices.length / invoices.length * 100) : 0;
                  
                  // Get method-specific icon and color
                  const getMethodIcon = (method: string) => {
                    switch (method) {
                      case 'Cash':
                        return { icon: <Wallet className="w-3 h-3 text-success" />, bg: 'bg-success/10' };
                      case 'UPI':
                        return { icon: <Smartphone className="w-3 h-3 text-primary" />, bg: 'bg-primary/10' };
                      case 'Card':
                        return { icon: <CreditCard className="w-3 h-3 text-warning" />, bg: 'bg-warning/10' };
                      case 'Bank Transfer':
                        return { icon: <Building2 className="w-3 h-3 text-primary" />, bg: 'bg-primary/10' };
                      default:
                        return { icon: <CreditCard className="w-3 h-3 text-primary" />, bg: 'bg-primary/10' };
                    }
                  };
                  
                  const methodIcon = getMethodIcon(method);
                  
                  return (
                    <div key={method} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className={cn("w-6 h-6 rounded-lg flex items-center justify-center", methodIcon.bg)}>
                          {methodIcon.icon}
                        </div>
                        <div>
                          <div className="font-semibold text-foreground text-sm">{method}</div>
                          <div className="text-xs text-muted-foreground">{methodInvoices.length} transactions</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-foreground text-sm">{formatCurrency(methodRevenue)}</div>
                        <div className="text-xs text-muted-foreground">{percentage.toFixed(1)}%</div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Revenue by Status */}
            <Card className="medical-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-foreground text-sm">
                  <div className="w-5 h-5 bg-primary/10 rounded-md flex items-center justify-center">
                    <BarChart3 className="w-3 h-3 text-primary" />
                  </div>
                  Revenue by Payment Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {['Paid', 'Pending', 'Partial'].map(status => {
                  const statusInvoices = invoices.filter(inv => inv.payment_status === status);
                  const statusRevenue = statusInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
                  const statusConfig = getStatusConfig(status);
                  
                  return (
                    <div key={status} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "w-6 h-6 rounded-lg flex items-center justify-center",
                          status === 'Paid' ? 'bg-success/10' :
                          status === 'Pending' ? 'bg-warning/10' :
                          'bg-primary/10'
                        )}>
                          <div className={cn(
                            "w-5 h-5",
                            status === 'Paid' ? 'text-success' :
                            status === 'Pending' ? 'text-warning' :
                            'text-primary'
                          )}>
                            {statusConfig.icon}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{status}</div>
                          <div className="text-xs text-muted-foreground">{statusInvoices.length} bills</div>
                        </div>
                      </div>
                      <div className="text-right">
                          <div className="font-bold text-foreground">{formatCurrency(statusRevenue)}</div>
                          <div className="text-xs text-muted-foreground">
                            {invoices.length > 0 ? (statusRevenue / invoices.reduce((sum, inv) => sum + inv.total_amount, 0) * 100).toFixed(1) : 0}%
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Enhanced Quick Statistics */}
          <Card className="medical-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-foreground">Performance Metrics</CardTitle>
              <p className="text-xs text-muted-foreground">Key performance indicators for your billing operations</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <CheckCircle className="w-4 h-4 text-success" />
                  </div>
                  <div className="text-xl font-bold text-success mb-1">
                    {analytics ? `${(analytics.paidInvoices / Math.max(analytics.totalInvoices, 1) * 100).toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="text-xs font-medium text-muted-foreground">Collection Rate</div>
                  <div className="text-xs text-muted-foreground mt-1">Bills successfully collected</div>
                </div>
                
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <IndianRupee className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-xl font-bold text-primary mb-1">
                    {analytics ? formatCurrency(analytics.averageInvoiceValue) : 'N/A'}
                  </div>
                  <div className="text-xs font-medium text-muted-foreground">Average Invoice</div>
                  <div className="text-xs text-muted-foreground mt-1">Mean transaction value</div>
                </div>
                
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Calendar className="w-4 h-4 text-warning" />
                  </div>
                  <div className="text-xl font-bold text-warning mb-1">
                    {invoices.filter(i => new Date(i.date) >= new Date(Date.now() - 7*24*60*60*1000)).length}
                  </div>
                  <div className="text-xs font-medium text-muted-foreground">This Week</div>
                  <div className="text-xs text-muted-foreground mt-1">Invoices created recently</div>
                </div>
                
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-xl font-bold text-primary mb-1">
                    {invoices.filter(i => new Date(i.date) >= new Date(Date.now() - 30*24*60*60*1000)).length}
                  </div>
                  <div className="text-xs font-medium text-muted-foreground">This Month</div>
                  <div className="text-xs text-muted-foreground mt-1">Monthly billing activity</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Trends */}
          <Card className="medical-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <div className="w-6 h-6 bg-success/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-3 h-3 text-success" />
                </div>
                Revenue Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-3 bg-success/10 rounded-lg">
                  <div className="text-lg font-bold text-success mb-1">
                    {analytics ? formatCurrency(analytics.totalRevenue) : formatCurrency(0)}
                  </div>
                  <div className="text-xs font-medium text-success">Total Revenue</div>
                  <div className="text-xs text-muted-foreground mt-1">All-time earnings</div>
                </div>
                
                <div className="text-center p-3 bg-primary/10 rounded-lg">
                  <div className="text-lg font-bold text-primary mb-1">
                    {analytics ? formatCurrency(analytics.monthlyRevenue) : formatCurrency(0)}
                  </div>
                  <div className="text-xs font-medium text-primary">Monthly Revenue</div>
                  <div className="text-xs text-muted-foreground mt-1">Current month earnings</div>
                </div>
                
                <div className="text-center p-3 bg-warning/10 rounded-lg">
                  <div className="text-lg font-bold text-warning mb-1">
                    {analytics ? formatCurrency(analytics.pendingAmount) : formatCurrency(0)}
                  </div>
                  <div className="text-xs font-medium text-warning">Outstanding</div>
                  <div className="text-xs text-muted-foreground mt-1">Awaiting payment</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-4">
          <BillingServicesTab
            clinicId={currentUser.clinic_id}
            doctorId={currentUser.doctor_id}
            onNotification={showNotification}
            showServiceModal={showServiceModal}
            onCloseServiceModal={() => {
              setShowServiceModal(false);
              setSelectedService(null);
            }}
            selectedService={selectedService}
            onServiceSaved={() => {
              setShowServiceModal(false);
              setSelectedService(null);
              loadServices(); // Refresh services list
            }}
            onEditService={(service) => {
              setSelectedService(service);
              setShowServiceModal(true);
            }}
          />
        </TabsContent>


      </Tabs>

      {/* Billing Modal — patient search is now inline */}
      {showCreateModal && (
        <MedicalBillingModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          patient={null}
          services={services}
          onCreateInvoice={handleCreateInvoice}
          loading={isLoading}
          clinicId={currentUser.clinic_id}
          doctorId={currentUser.doctor_id}
          doctors={currentUser?.role === 'staff' ? billingDoctors : undefined}
          onRefreshServices={loadServices}
        />
      )}

      {/* View Invoice Modal */}
      <InvoiceViewModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        invoice={selectedInvoice}
        onPrint={handlePrintInvoice}
        onPayment={handlePayInvoice}
        variant="clinic"
      />

      {/* Payment Modal */}
      <PaymentRecordModal
        isOpen={showPaymentModal}
        onClose={() => {
                setShowPaymentModal(false);
                setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onRecordPayment={(amount, method) => {
          handleRecordPayment(amount, method);
            }}
            loading={isLoading}
          />

      {/* Invoice Edit Modal */}
      <InvoiceEditModal
        invoice={selectedInvoice ? {
          ...selectedInvoice,
          items: selectedInvoice.items || []
        } : null}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedInvoice(null);
        }}
        onSave={handleUpdateInvoice}
        services={services}
        loading={isLoading}
      />

      {/* Print Bill Preview Modal */}
      <PrintBillPreviewModal
        open={showPrintModal}
        invoice={selectedInvoice}
        onClose={() => {
          setShowPrintModal(false);
          setSelectedInvoice(null);
        }}
      />
    </div>
  );
}